import { Box, Stack, Typography } from '@mui/material'
import { FeatureFlagIdBranding } from 'c1gen/features'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1Link } from 'components/core/links/C1Link'
import { useFeatureChecker } from 'components/no-ui/capability'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { C1AppBarStyles } from 'components/pages/app-bar/styles'
import { AdminModeContext } from 'lib/context/app'
import { routes } from 'lib/routes'
import { BrandingConfiguration } from 'pbts/c1/api/settings/v1/branding'
import { IntrospectResponse_TenantLifecycle } from 'pbts/c1/api/tenant/v1/tenant'
import { IntrospectRequest } from 'pbts/c1/api/user/v1/user'
import { FC, useContext } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { introspectBrandingApi } from 'redux_toolkit/branding'
import { introspectTenantApi } from 'redux_toolkit/tenant'

const getTenantLifecycleLabel = ({
    lifecycle, intl,
}: {
    lifecycle?: IntrospectResponse_TenantLifecycle
    intl: IntlShape
}): string => {
    // TODO(pquerna): do we want a rendering for POC?
    switch (lifecycle) {
        case IntrospectResponse_TenantLifecycle.ADMIN_DISABLED:
            return ''
        case IntrospectResponse_TenantLifecycle.LIVE_POC:
            return ''
        case IntrospectResponse_TenantLifecycle.LIVE_SANDBOX:
            return intl.formatMessage({ id: 'sandbox_chip', defaultMessage: 'Sandbox' })
        case IntrospectResponse_TenantLifecycle.LIVE_PRODUCTION:
            return ''
        default:
            return ''
    }
}

const MOBILE_LOGO_URL = '/dapi/static/images/branding/favicon-white/current/32'
const DESKTOP_LOGO_URL = '/dapi/static/images/branding/logo-white/current/256'

const getMobileLogoSrc = (brandingData: BrandingConfiguration | undefined, hasBrandingFF: boolean | null): string => {
    return !hasBrandingFF
        ? MOBILE_LOGO_URL
        : `${MOBILE_LOGO_URL}?${brandingData?.faviconWhite?.src || brandingData?.favicon?.src}&${brandingData?.enabled}`
}

const getDesktopLogoSrc = (brandingData: BrandingConfiguration | undefined, hasBrandingFF: boolean | null): string => {
    return !hasBrandingFF
        ? DESKTOP_LOGO_URL
        : `${DESKTOP_LOGO_URL}?${brandingData?.logoWhite?.src || brandingData?.logo?.src}&${brandingData?.enabled}`
}

export const ConductorLogoLockup: FC = () => {
    const adminModeContext = useContext(AdminModeContext)
    const intl = useIntl()

    const {
        data: tenantData,
    } = introspectTenantApi.useIntrospect(IntrospectRequest.create())

    const hasBrandingFF = useFeatureChecker(FeatureFlagIdBranding)

    const tenantLifecycleLabel = getTenantLifecycleLabel({
        lifecycle: tenantData?.lifecycle,
        intl,
    })

    // We call this just so that we can bust the cache when the branding data changes.
    const { data: brandingData } = introspectBrandingApi.useIntrospect({}, { notify: true })

    return <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{
            backgroundColor: !tenantLifecycleLabel
                ? 'transparent'
                : 'rgba(0,0,0,0.8)',
            borderRadius: C1BorderRadius.large,
            py: 1,
            pl: 2,
            pr: tenantLifecycleLabel ? 1 : 0,
        }}
    >
        <Box sx={sxc(C1AppBarStyles.hideInSmallScreen)}>
            <C1Link
                href={routes.home}
                aria-label={intl.formatMessage(
                    { id: 'components_app_bar_home_link', defaultMessage: 'Go to {displayname} home' },
                    { displayname: brandingData?.displayName || 'ConductorOne' },
                )}
            >
                <Box
                    component="img"
                    alt={brandingData?.displayName || 'ConductorOne'}
                    src={getDesktopLogoSrc(brandingData, hasBrandingFF)}
                    height={20}
                    style={{ display: 'block' }}
                />
            </C1Link>
        </Box>
        <Box sx={sxc(C1AppBarStyles.mobileLogo, C1AppBarStyles.showInSmallScreen)}>
            <C1Link
                href={routes.home}
                aria-label={intl.formatMessage(
                    { id: 'components_app_bar_home_link', defaultMessage: 'Go to {displayname} home' },
                    { displayname: brandingData?.displayName || 'ConductorOne' },
                )}
            >
                <Box
                    component="img"
                    alt={brandingData?.displayName || 'ConductorOne'}
                    src={getMobileLogoSrc(brandingData, hasBrandingFF)}
                    height={30}
                    style={{ display: 'block' }}
                />
            </C1Link>
        </Box>

        {tenantLifecycleLabel && <C1Chip
            sx={{
                backgroundColor: adminModeContext.isOnAdminPath
                    ? 'primary.extraDark'
                    : 'brand.dark',
                border: 'none',
            }}
            label={<Typography color="warning.light" sx={{ fontSize: 11 }} variant="eyebrow">{tenantLifecycleLabel}</Typography>}
        />}
    </Stack>
}
